import {Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, shareReplay} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

export interface ChangelogEntry {
  tag: string;
  name: string;
  published_at: string;
  html_url: string;
  headline: string;
  highlights: string[];
}

export interface Changelog {
  environment: string;
  entries: ChangelogEntry[];
}

/** Records when the user last saw the changelog, so a new release can pop it up again. */
const SEEN_KEY = 'cp-whats-new-seen';
/** Earlier versions stored the marker in a cookie of the same name; expire it. */
const LEGACY_COOKIE = SEEN_KEY;

@Injectable({providedIn: 'root'})
export class WhatsNewService {
  readonly entries = signal<ChangelogEntry[] | null>(null);
  /** True while the newest release is newer than the last time the user saw the changelog. */
  readonly hasUnseen = signal(false);

  private readonly changelog$: Observable<ChangelogEntry[]>;

  constructor(private http: HttpClient) {
    this.removeLegacyCookie();
    // The changelog is rewritten on every deploy and decides whether the popover
    // opens, so a cached copy would hide fresh release notes. CloudFront serves
    // /assets/changelog.json with `Cache-Control: no-store` (see portal_stack.py);
    // the cache buster covers every other host (local ng serve, direct S3).
    this.changelog$ = this.http.get<Changelog>(`/assets/changelog.json?t=${Date.now()}`).pipe(
      map(log => log?.entries ?? []),
      catchError(() => of<ChangelogEntry[]>([])),
      tap(entries => {
        this.entries.set(entries);
        this.hasUnseen.set(this.isUnseen(entries));
      }),
      shareReplay(1)
    );
  }

  /** Loads the changelog once per session; replays to later subscribers. */
  load(): Observable<ChangelogEntry[]> {
    return this.changelog$;
  }

  /** Remembers that the changelog was displayed now, so it won't pop up again. */
  markSeen(): void {
    // localStorage, not a cookie: the value never leaves the browser, so it
    // needs no consent under GDPR/ePrivacy. Storage can throw (private mode,
    // blocked site data); then the popover simply shows again next visit.
    try {
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
    } catch {
      // ignore
    }
    this.hasUnseen.set(false);
  }

  private removeLegacyCookie(): void {
    document.cookie = `${LEGACY_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  }

  private isUnseen(entries: ChangelogEntry[]): boolean {
    const latest = this.latestRelease(entries);
    if (latest === null) {
      return false;
    }
    const lastSeen = this.lastSeen();
    // Never seen it: show it once, so the entry point is discovered.
    return lastSeen === null || latest > lastSeen;
  }

  private latestRelease(entries: ChangelogEntry[]): number | null {
    const timestamps = entries
      .map(entry => Date.parse(entry.published_at))
      .filter(ts => !isNaN(ts));
    return timestamps.length > 0 ? Math.max(...timestamps) : null;
  }

  private lastSeen(): number | null {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(SEEN_KEY);
    } catch {
      return null;
    }
    if (!stored) {
      return null;
    }
    const seen = Date.parse(stored);
    return isNaN(seen) ? null : seen;
  }
}